import Head from "next/head";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import {
  ShoppingCartIcon,
  FlagIcon,
  CalendarIcon,
  LocationMarkerIcon,
  UserGroupIcon,
  StatusOnlineIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  TicketIcon as EventIcon,
} from "@heroicons/react/solid";
import cx from "classnames";
import Link from "next/link";
import Image from "next/image";
import { TicketIcon, ReceiptTaxIcon } from "@heroicons/react/outline";
import useLocalStorage from "hooks/useLocalStorage";
import { Expert, EventItem } from "types/user";
import Tippy from "@tippyjs/react";
import {
  getFullName,
  getLinkFromString,
  handleClipboard,
  truncateString,
  isFacebookSupportedImage,
  getFormattedTimeZone,
  findCountryByCode,
  getRemainingTime,
  clientTimeZone,
  trackPageView,
  trackViewContent,
  cloudfront,
  toast,
  makeUrlsClickable,
  roundTo,
  getTimeFormat,
  formatEventPlaceLabel,
  normalizeTrackingId,
} from "utils"; // import Breadcrumbs from "components/server/Breadcrumbs";
import Button from "components/Button/Button";
import OfferShareButton from "components/OfferShareButton/OfferShareButton";
import OfferAttachment from "components/OfferAttachment/OfferAttachment";
import EventCoverPlayer from "components/EventCoverPlayer/EventCoverPlayer";
import { OfferSignIn } from "components/OfferSignIn";
import { HourglassIcon } from "components/Icons";
import { formattedDate, getEventStatus } from "utils/timeZoneUtils";
import { OfferSignInUpOtp } from "components/OfferSignInUpOtp";
import { HeartIcon } from "@heroicons/react/outline";
import Script from "next/script";
import { useTranslation } from "react-i18next";
// import ChatIcon from "components/Icons/ChatIcon/ChatIcon";
import { useMutation } from "@tanstack/react-query";
import { api } from "utils/api";
import Modal from "components/Modal/Modal";
import PromotionModalActions from "components/PromotionModalActions/PromotionModalActions";
import EventLocationSection from "components/EventLocationSection/EventLocationSection";
import { ReadableEventLocationText } from "components/EventLocationInput/ReadableEventLocationText";
import EventRefundPolicyNotice from "components/EventSpecSections/EventRefundPolicyNotice";
import EventOrganizerLinks from "components/EventSpecSections/EventOrganizerLinks";
import {
  ASSIGNED_SEATING_BUY_FLOW,
  eventBuyFlowIntent,
} from "screens/browser/client/offer/Event/eventBuyFlow";
import AssignedSeatingPicker from "screens/browser/client/offer/Event/components/AssignedSeatingPicker";
import EventPromotionAction from "screens/browser/client/offer/Event/components/EventPromotionAction";
import { getEventLevels } from "screens/browser/client/offer/Event/models/eventTicketSelection";
import {
  AssignedSeatingAvailabilityIcon,
  AssignedSeatingTicketPlanList,
  EventTicketAvailabilityIndicator,
} from "screens/browser/client/offer/Event/components/EventTicketPurchasePanel";
import {
  AssignedSeatingMap,
  areAssignedSeatingTicketTypesSoldOut,
  buildSeatHoldPayload,
  formatAssignedSeatingSeatsLeft,
  getAssignedSeatingTicketTypes,
  isAssignedSeatingBuyerEvent,
  isAssignedSeatingLevelSoldOut,
  selectedAssignedSeats,
  toggleAssignedSeat,
} from "screens/browser/client/offer/Event/models/assignedSeating";
import {
  normalizePendingAssignedSeatKeys,
  pendingAssignedSeatSelectionStorageKey,
} from "screens/browser/client/offer/Event/models/pendingAssignedSeatSelection";
// import Cookies from "js-cookie";

export const PublicAssignedSeatingPurchasePanel = ({
  event,
  promotionCode,
  countryCurrency,
  isLevelSoldOut,
  assignedSeatingTicketTypes,
  isSoldOut,
  isMobile,
  onOpen,
  handleOpenPromoModal,
  translate,
}: {
  event?: EventItem;
  promotionCode?: any;
  countryCurrency: ReturnType<typeof findCountryByCode>;
  isLevelSoldOut: (level: NonNullable<EventItem["levels"]>[number]) => boolean;
  assignedSeatingTicketTypes: ReturnType<typeof getAssignedSeatingTicketTypes>;
  isSoldOut: boolean;
  isMobile: boolean;
  onOpen: () => void;
  handleOpenPromoModal: () => void;
  translate: (key: string) => string;
}) => {
  const availabilityLabel = formatAssignedSeatingSeatsLeft(
    assignedSeatingTicketTypes,
    translate
  );

  return (
    <div
      className="flex flex-col items-center justify-center"
      data-testid="assigned-seating-public-panel"
    >
      <AssignedSeatingTicketPlanList
        isMobile={isMobile}
        event={event}
        promotionCode={promotionCode}
        countryCurrency={countryCurrency}
        assignedSeatingTicketTypes={assignedSeatingTicketTypes}
        onOpen={onOpen}
        translate={translate}
      />
      {isSoldOut ? (
        <Button
          className="w-3/4"
          icon={<TicketIcon className="h-6 w-6" />}
          type="sold"
          disabled={true}
          fluid
        >
          {translate("soldOut")}
        </Button>
      ) : (
        <>
          <div
            className="inline-flex w-3/4 justify-center"
            data-testid={
              isMobile
                ? "assigned-seating-public-cta-mobile"
                : "assigned-seating-public-cta"
            }
          >
            <Button
              className="w-full"
              icon={<TicketIcon className="h-6 w-6" />}
              type="danger"
              onClick={onOpen}
              fluid
            >
              {translate("buy")}
            </Button>
          </div>
          {!promotionCode ? (
            <EventPromotionAction
              label={translate("enterPromoCodePromotions")}
              onClick={handleOpenPromoModal}
            />
          ) : null}
        </>
      )}
      <div className="mt-4 mb-2 flex items-center justify-center">
        <AssignedSeatingAvailabilityIcon />
        <span className="text-sm">{availabilityLabel}</span>
      </div>
    </div>
  );
};

const EventPage = ({
  expert,
  event,
  query,
  assignedSeatingMap,
}: {
  expert: Expert;
  event: EventItem;
  query;
  assignedSeatingMap?: AssignedSeatingMap | null;
}) => {
  const router = useRouter();
  const publicId = router.query.publicId as string;
  const eventId = router.query.eventId as string;
  const [hasAuthForm, setAuthForm] = useState<"sign-in" | "sign-up">(null);
  const authEmailInputsRef = useRef<Set<HTMLInputElement>>(new Set());
  const registerAuthEmailInput = useCallback(
    (emailInput: HTMLInputElement | null) => {
      if (emailInput) {
        authEmailInputsRef.current.add(emailInput);
      }
    },
    []
  );
  const [, setEventBuyFlow] = useLocalStorage(`event-${eventId}`, null);
  const [, setPendingAssignedSeatKeys] = useLocalStorage<string[]>(
    pendingAssignedSeatSelectionStorageKey(eventId),
    []
  );
  const [timeFormat, setTimeFormat] = useState(getTimeFormat(expert?.country));
  const trackingMetaPixelId =
    normalizeTrackingId(event?.metapixelid) ||
    normalizeTrackingId(expert?.metapixelid);
  const trackingGtmId =
    normalizeTrackingId(event?.gtmid) || normalizeTrackingId(expert?.gtmid);
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const [, setSelectedPlanIndex] = useLocalStorage(
    `eventSelectIndex-${eventId}`,
    null
  );
  const [, setCountStorage] = useLocalStorage(`eventCount-${eventId}`, null);
  const [isChatOpenTrue, setOpenChat] = useLocalStorage(
    `event-chat-${eventId}`,
    null
  );
  const [isFollowTrue, setFollowChat] = useLocalStorage(
    `event-follow-${eventId}`,
    null
  );
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [promo, setPromo] = useState("");
  const [code, setCode] = useState(
    typeof router.query.promo === "string" ? router.query.promo : ""
  );
  const [promotionCode, setPromotionCode] = useState<any>(undefined);
  const [selectedPlan, setSelectedPlan] = useState<number>(0);
  const [countEvent, setCounts] = useState<number[]>([0]);
  const [isSeatPickerOpen, setSeatPickerOpen] = useState(false);
  const [selectedSeatKeys, setSelectedSeatKeys] = useState<string[]>([]);
  const isAssignedSeatingEvent = isAssignedSeatingBuyerEvent({ event });
  const selectedSeats = selectedAssignedSeats({
    seatMap: assignedSeatingMap,
    selectedSeatKeys,
    levels: event?.levels || [],
  });
  const assignedSeatingTicketTypes = getAssignedSeatingTicketTypes({
    seatMap: assignedSeatingMap,
  });

  function promoModalClose() {
    setPromo("");
    setIsPromoModalOpen(false);
  }
  const handleCheckPromotion = async () => {
    try {
      await checkPromotion({
        code: promo,
        offerid: event?.offerId,
      });
    } catch (error) {
      setCode("");
      setPromo("");
      console.error("Error checking promotion:", error);
    }
  };

  const handleOpenPromoModal = () => {
    setIsPromoModalOpen(true);
  };

  useEffect(() => {
    if (!hasAuthForm) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      authEmailInputsRef.current.forEach((emailInput) => {
        if (!emailInput.isConnected) {
          authEmailInputsRef.current.delete(emailInput);
        }
      });
      const activeEmailInput = Array.from(authEmailInputsRef.current).find(
        (emailInput) =>
          emailInput.isConnected && emailInput.getClientRects().length > 0
      );

      activeEmailInput?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      activeEmailInput?.focus({ preventScroll: true });
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [hasAuthForm]);

  const { mutateAsync: checkPromotion, isLoading: checkPromotionLoader } =
    useMutation(
      (payload: { code: string; offerid?: string }) =>
        api.post("/experts/v1/experts/check-promotion", payload),
      {
        onSuccess: (data) => {
          setCode(promo);
          // Cookies.set("promo", promo);
          // Cookies.set("slug", event?.offerSlug);
          if (
            data?.status === "PROMOTION_NOT_FOUND" ||
            data?.status === "PROMOTION_LIMIT_REACHED" ||
            data?.status === "PROMOTION_EXPIRED" ||
            data?.status === "PROMOTION_DISABLED" ||
            data?.status === "PROMOTION_DELETED"
          ) {
            setCode("");
            toast(
              {
                title: t("promoCodeIncorrectExpired"),
                icon: "error",
              },
              { type: "custom" }
            );
          } else {
            setCode(promo);
            setPromotionCode(data);
            toast(t("promoCodeAddedToast"), { type: "success" });
            router.push(`/${publicId}/event/${eventId}?promo=${promo}`);
          }
          promoModalClose();
        },
        onError: (error) => {
          console.log(error);

          setCode("");
          promoModalClose();
          toast(
            {
              title: t("promoCodeIncorrectExpired"),
              icon: "error",
            },
            { type: "custom" }
          );
        },
      }
    );

  const { mutateAsync: promotion, isLoading: promotionLoadingCode } =
    useMutation(
      (payload: { code: string | any; offerid?: string }) =>
        api.post("/experts/v1/experts/check-promotion", payload),
      {
        onSuccess: (data) => {
          if (
            data?.status === "PROMOTION_NOT_FOUND" ||
            data?.status === "PROMOTION_LIMIT_REACHED" ||
            data?.status === "PROMOTION_EXPIRED" ||
            data?.status === "PROMOTION_DISABLED" ||
            data?.status === "PROMOTION_DELETED"
          ) {
            handleLeavePage();
            setCode("");
            toast(
              {
                title: t("promoCodeIncorrectExpired"),
                icon: "error",
              },
              { type: "custom" }
            );
            router.push(`/${publicId}/event/${eventId}`);
          } else {
            setPromotionCode(data);
            router.push(
              `/${publicId}/event/${eventId}` + (code ? `?promo=${code}` : "")
            );
          }
        },
        onError: (error) => {
          handleLeavePage();
          setCode("");
          // Cookies.remove("promo");
          // Cookies.remove("slug");
          toast(
            {
              title: t("promoCodeIncorrectExpired"),
              icon: "error",
            },
            { type: "custom" }
          );
        },
      }
    );

  useEffect(() => {
    if (code) {
      checkPromo();
    }
    if (event) {
      setLoading(false);
    }
    setIsMounted(true);
  }, []);

  const handleLeavePage = () => {
    const { promo, ...query } = router.query;
    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  const checkPromo = async () => {
    try {
      await promotion({
        code: code,
        offerid: event?.offerId,
      });
    } catch (error) {
      console.error("Error checking promotion:", error);
    }
  };

  useEffect(() => {
    if (trackingMetaPixelId) {
      trackPageView(trackingMetaPixelId, event?.offerSlug, event?.name);
    }
  }, [trackingMetaPixelId, event?.offerSlug, event?.name]);

  useEffect(() => {
    if (event) {
      trackViewContent(
        event?.offerSlug,
        "event",
        event?.name,
        1,
        event?.levels[selectedPlan]?.price,
        event?.currency,
        expert?.publicId,
        trackingMetaPixelId,
        trackingGtmId
      );
    }
  }, [
    event,
    expert?.publicId,
    selectedPlan,
    trackingGtmId,
    trackingMetaPixelId,
  ]);

  const countryCurrency = findCountryByCode(event?.currency);
  const IconComponent: any = countryCurrency?.icon;

  const updateCounts = (levels) => {
    const counts = levels.map((level, index) => {
      if (!isLevelSoldOut(level)) {
        return index === levels.findIndex((lvl) => !isLevelSoldOut(lvl))
          ? 1
          : 0;
      }
      return 0;
    });

    setCounts(counts);
  };

  useEffect(() => {
    if (event) {
      const firstAvailablePlan = event?.levels.findIndex(
        (level) => !isLevelSoldOut(level)
      );
      setSelectedPlan(firstAvailablePlan !== -1 ? firstAvailablePlan : 0);
      updateCounts(event.levels);
    }
  }, [event]);

  const handlePlanChange = (index) => {
    if (event?.levels[index] && !isLevelSoldOut(event?.levels[index])) {
      setSelectedPlan(index);
    }
  };
  const incrementCount = (index) => {
    setCounts(
      countEvent.map((count, i) => {
        if (i !== index || !event?.levels[i]) {
          return count;
        }
        if (isLevelSoldOut(event?.levels[i])) {
          return count;
        }
        return count < getRemainingSeats(event?.levels[i]) ? count + 1 : count;
      })
    );
  };

  const decrementCount = (index) => {
    setCounts(
      countEvent.map((count, i) =>
        i === index && count > 0 ? count - 1 : count
      )
    );
  };
  function calculateDiscountedPrice(originalPrice, discountPercent) {
    let discountAmount = originalPrice * (discountPercent / 100);
    let discountedPrice = originalPrice - discountAmount;
    return discountedPrice;
  }

  const levelsWithDiscounts = event?.levels.map((level) => {
    const discountLevel = promotionCode?.levels_discount.find(
      (discount) => discount.levelid === level.levelid
    );

    if (discountLevel) {
      return {
        ...level,
        price:
          Math.round(
            calculateDiscountedPrice(level.price, discountLevel.discount) * 100
          ) / 100,
      };
    }

    return level;
  });

  const totalSumWithoutRounding = levelsWithDiscounts?.reduce(
    (total, level, index) => {
      const price = level?.price || 0;
      const count = countEvent[index] || 0;
      return total + price * count;
    },
    0
  );

  const totalSum = Math.ceil(totalSumWithoutRounding * 100) / 100;
  const isOfflineEvent = event?.eventType === "OfflineEvent";

  const normalizeUtcDateString = (value?: string) => {
    if (!value) {
      return null;
    }
    if (/[zZ]$/.test(value) || /[+-]\d{2}:\d{2}$/.test(value)) {
      return value;
    }
    return `${value}.000Z`;
  };

  const getTicketSalesEndDate = (level) => level?.endDate || null;

  const getLevelSeatsCount = (level) => {
    if (level?.seats === null || typeof level?.seats === "undefined") {
      return null;
    }
    const seats =
      typeof level.seats === "number" ? level.seats : parseInt(level.seats);
    return Number.isNaN(seats) ? null : seats;
  };

  const getLevelSoldCount = (level) => {
    const sold =
      typeof level?.sold === "number"
        ? level.sold
        : parseInt(level?.sold || "0");
    return Number.isNaN(sold) ? 0 : sold;
  };

  const isLevelEndDateExpired = (level) => {
    if (!isOfflineEvent) {
      return false;
    }
    const ticketSalesEndDate = getTicketSalesEndDate(level);
    const normalizedEndDate = normalizeUtcDateString(ticketSalesEndDate);
    if (!normalizedEndDate) {
      return false;
    }
    const endTime = new Date(normalizedEndDate).getTime();
    if (Number.isNaN(endTime)) {
      return false;
    }
    return Date.now() > endTime;
  };

  const isLevelSoldOut = (level) => {
    if (isAssignedSeatingEvent) {
      return isAssignedSeatingLevelSoldOut({
        level,
        ticketTypes: assignedSeatingTicketTypes,
      });
    }

    const seats = getLevelSeatsCount(level);
    const sold = getLevelSoldCount(level);
    const seatsExhausted =
      seats === null || typeof seats === "undefined" ? false : sold >= seats;
    return seatsExhausted || isLevelEndDateExpired(level);
  };

  const getRemainingSeats = (level) => {
    const seats = getLevelSeatsCount(level);
    if (seats === null) {
      return 0;
    }
    return seats - getLevelSoldCount(level);
  };

  function seatsLeftAllLevels(
    levels: { seats?: string; sold?: number }[]
  ): string {
    if (!levels) {
      return `0 ${t("seatsLeft")}`;
    }
    let totalSeats = 0;
    let totalSold = 0;
    levels.forEach((level) => {
      if (isOfflineEvent && isLevelEndDateExpired(level)) {
        return;
      }
      totalSeats += getLevelSeatsCount(level) || 0;
      totalSold += getLevelSoldCount(level);
    });
    return `${totalSeats - totalSold} ${t("seatsLeft")} `;
  }

  function isSoldOut(
    levels: { seats?: string; sold?: string | number }[]
  ): boolean {
    if (isAssignedSeatingEvent) {
      return areAssignedSeatingTicketTypesSoldOut(assignedSeatingTicketTypes);
    }
    if (!levels) {
      return false;
    }
    const activeLevels = isOfflineEvent
      ? levels.filter((level) => !isLevelEndDateExpired(level))
      : levels;
    const totalSeats = activeLevels.reduce((sum, level) => {
      const seats = getLevelSeatsCount(level) || 0;
      const sold = getLevelSoldCount(level);
      return sum + (seats - sold);
    }, 0);

    return totalSeats <= 0;
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleCopy = () => {
    handleClipboard({
      text: `${publicId}/event/${eventId}`,
      toastText: "toastOfferUrlCopied",
    });
  };

  const openChat = () => {
    setOpenChat(true);
    setAuthForm("sign-up");
  };
  const handleFollow = () => {
    setFollowChat(true);
    setAuthForm("sign-up");
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query.p === "") {
      if (!isChatOpenTrue || !isFollowTrue) {
        setSelectedPlanIndex(selectedPlan);
        setCountStorage(countEvent);
        setEventBuyFlow(eventBuyFlowIntent(isAssignedSeatingEvent));
        setAuthForm("sign-up");
      }
    }
  }, [query, isAssignedSeatingEvent]);

  const handleBuy = () => {
    if (!isChatOpenTrue || !isFollowTrue) {
      setSelectedPlanIndex(selectedPlan);
      setCountStorage(countEvent);
      setEventBuyFlow(eventBuyFlowIntent(isAssignedSeatingEvent));
      setAuthForm("sign-up");
    }
  };

  const handleAnonymousSeatToggle = (seatKey: string) => {
    setSelectedSeatKeys((current) =>
      toggleAssignedSeat({
        seatKey,
        selectedSeatKeys: current,
        seatMap: assignedSeatingMap,
      })
    );
  };

  const handleAnonymousSeatBuy = () => {
    const stableSeatKeys = selectedAssignedSeats({
      seatMap: assignedSeatingMap,
      selectedSeatKeys: buildSeatHoldPayload({
        seatMap: assignedSeatingMap,
        selectedSeatKeys,
      }).seatKeys,
      levels: event?.levels || [],
    }).map((seat) => seat.seatKey);

    setSelectedSeatKeys(stableSeatKeys);
    if (!stableSeatKeys.length) {
      toast(t("selectSeatsFirst"), { type: "error" });
      return;
    }

    setPendingAssignedSeatKeys(
      normalizePendingAssignedSeatKeys(stableSeatKeys)
    );
    setEventBuyFlow(ASSIGNED_SEATING_BUY_FLOW);
    setSeatPickerOpen(false);
    setAuthForm("sign-up");
  };

  const isOnlineEvent = event?.eventType === "OnlineEvent";
  const isOnlineTelegramEvent = event?.eventType === "OnlineTelegramEvent";
  const shouldShowTimeZoneCity =
    (isOnlineEvent || isOnlineTelegramEvent) && Boolean(event?.city);

  const eventPlaceLabel = formatEventPlaceLabel(
    event?.eventType,
    event?.sections?.location,
    event?.sections?.address,
    t,
    event?.city,
    event?.sections?.locationDetails,
    {
      eventDate: event?.eventDate,
      isPurchased: false,
      locationVisibility: event?.sections?.locationVisibility,
    }
  );

  const { firstName, lastName, avatar, freeChat, isOnline, country } = expert;

  const fullName = getFullName(firstName, lastName, publicId);
  const isFreeEvent =
    event?.levels[selectedPlan].price === 0.0 || totalSum === 0;

  if (!event) {
    return (
      <div className="flex items-center justify-center w-full h-screen -mt-16 pt-16 text-center">
        <div className="flex flex-col items-center max-w-sm">
          <Image
            src="/img/empty-expert.svg"
            width={128}
            height={128}
            alt="no expert"
          />
          <h3 className="font-bold mt-20 text-lg">{t("emptyExpertTitle")}</h3>
          <p className="text-sm font-medium opacity-50 mt-4">
            {t("emptyExpertText")}
          </p>
        </div>
      </div>
    );
  }

  const title = `${event?.name ?? ""} - ${fullName}`;
  const description = truncateString(event?.description, 150);

  const cover = event?.cover;
  const avatarOrGeneric = isFacebookSupportedImage(avatar)
    ? avatar
    : "https://waylight.me/wlgenericpreview.png";
  const isProperImage = isFacebookSupportedImage(cover);

  const transformedDescription = getLinkFromString(event?.description);

  const timeZoneGMT = getFormattedTimeZone(timeZone).split(" ")[0].slice(1, -1);

  const hotjarid = expert?.hotjarid;
  const isSoldEnabled = expert?.isSoldEnabled;

  const { hasStarted, isOngoing, isEnded } =
    event?.eventDate && event?.eventDateEnd && timeZone
      ? getEventStatus(event.eventDate, event.eventDateEnd, timeZone)
      : { hasStarted: false, isOngoing: false, isEnded: false };

  const selectedLevelId = event?.levels[selectedPlan]?.levelid;

  const discountForSelectedLevel = promotionCode?.levels_discount.find(
    (discount) => discount.levelid === selectedLevelId
  );

  let priceAfterDiscount = event?.levels[selectedPlan]?.price;

  if (discountForSelectedLevel) {
    priceAfterDiscount =
      Math.round(
        calculateDiscountedPrice(
          event?.levels[selectedPlan]?.price,
          discountForSelectedLevel.discount
        ) * 100
      ) / 100;
  } else if ((isOnlineEvent || isOnlineTelegramEvent) && promotionCode) {
    priceAfterDiscount =
      Math.round(
        calculateDiscountedPrice(
          event?.levels[selectedPlan]?.price,
          promotionCode?.offer_discount
        ) * 100
      ) / 100;
  }

  const totalSumWithoutDiscount = event?.levels.reduce(
    (total, level, index) => {
      const price = level?.price || 0;
      const count = countEvent[index] || 0;
      return total + price * count;
    },
    0
  );

  const totalDiscount = roundTo(totalSumWithoutDiscount - totalSum, 2);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content={
            cover
              ? isProperImage
                ? cloudfront + cover
                : avatarOrGeneric
              : avatarOrGeneric
          }
        />

        <meta
          property="og:url"
          content={`https://waylight.me/${publicId}/event/${event?.offerSlug}`}
        />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta
          property="twitter:image"
          content={
            cover
              ? isProperImage
                ? cloudfront + cover
                : avatarOrGeneric
              : avatarOrGeneric
          }
        />
        <meta property="twitter:card" content="summary" />
        {trackingGtmId ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `
     window.dataLayer = window.dataLayer || [];
     function gtag(){dataLayer.push(arguments);}
     gtag('js', new Date());
     gtag('config', '${trackingGtmId}');
   `,
            }}
          />
        ) : null}
      </Head>

      {trackingGtmId ? (
        <Script
          id="profilegtm"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                 new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                 j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                 })(window,document,'script','dataLayer','${trackingGtmId}');`,
          }}
        />
      ) : null}

      {hotjarid ? (
        <Script
          id="profilehotjar"
          dangerouslySetInnerHTML={{
            __html: `(function(h,o,t,j,a,r){
                           h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                           h._hjSettings={hjid:${hotjarid},hjsv:6};
                           a=o.getElementsByTagName('head')[0];
                           r=o.createElement('script');r.async=1;
                           r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                           a.appendChild(r);
                       })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
          }}
        />
      ) : null}

      {trackingMetaPixelId ? (
        <Script
          id="profilemetanew"
          dangerouslySetInnerHTML={{
            __html: `!function(f,b,e,v,n,t,s)
                                       {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                                       n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                                       if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                                       n.queue=[];t=b.createElement(e);t.async=!0;
                                       t.src=v;s=b.getElementsByTagName(e)[0];
                                       s.parentNode.insertBefore(t,s)}(window, document,'script',
                                       'https://connect.facebook.net/en_US/fbevents.js');
                                       fbq('init', '${trackingMetaPixelId}');
                             
                                       `,
          }}
        />
      ) : null}

      <div
        className={cx("mx-auto px-4", {
          "max-w-7xl xl:px-8":
            hasAuthForm || (!isEnded && isOfflineEvent && !hasAuthForm),
          "max-w-3xl lg:max-w-4xl lg:px-14 pb-20":
            isEnded ||
            ((isOnlineEvent || isOnlineTelegramEvent) && !hasAuthForm),
        })}
      >
        {(!isOfflineEvent || isEnded) && (
          <div className="hidden xl:flex md:flex justify-end items-center pt-6">
            {loading ? null : !hasStarted ? (
              <button className="rounded-full hidden md:flex md:w-auto px-6 w-full lg:w-auto justify-center mb-4 md:mb-0 lg:mb-0 py-2 text-xs font-semibold bg-yellow-300 items-center xl:flex">
                <HourglassIcon color="black" className="shrink-0 w-4 h-4" />
                {getRemainingTime(event?.eventDate, timeZone)}
              </button>
            ) : isOngoing ? (
              <div className="mb-4 md:mb-0 xl:m-0 lg:mb-0 text-right ml-auto block md:flex lg:flex">
                <button className="rounded-full px-6 py-2 text-xs font-semibold  bg-green-500 items-center flex">
                  <StatusOnlineIcon className="w-4 h-4 text-slate-50" />
                  <span className="ml-2 text-white">{t("eventIsLife")}</span>
                </button>
              </div>
            ) : (
              <div className="mb-4 md:mb-0 lg:mb-0 text-right ml-auto block md:flex lg:flex">
                <button className="rounded-full px-6 py-2 text-xs font-semibold  bg-slate-100 items-center flex">
                  <FlagIcon className="w-4 h-4" />
                  <span className="ml-1">{t("passed")}</span>
                </button>
              </div>
            )}
          </div>
        )}
        <div className="xl:hidden md:hidden hidden">
          {/* <Breadcrumbs
            pages={[
              { name: t("explore"), url: "/" },
              {
                name: fullName,
                url: `/${publicId}#events`,
              },
              { name: t("event") },
            ]}
          /> */}
          {loading ? null : !hasStarted ? (
            <button
              className={`rounded-full w-auto justify-center mt-4 mb-0 lg:mb-0 py-2 text-xs font-semibold bg-yellow-300 items-center ml-auto px-4 flex ${
                isOfflineEvent && !hasAuthForm ? "hidden" : ""
              }`}
            >
              <HourglassIcon color="black" className="shrink-0 w-4 h-4" />
              <span className="ml-1">
                {getRemainingTime(event?.eventDate, timeZone)}
              </span>
            </button>
          ) : !isOngoing ? (
            <div className="mt-4 mb-0  lg:mb-0 text-right ml-auto block md:hidden lg:hidden">
              <button className="rounded-full px-6 py-2 text-xs font-semibold ml-auto bg-slate-100 items-center flex">
                <FlagIcon className="w-4 h-4" />
                <span className="ml-1">{t("passed")}</span>
              </button>
            </div>
          ) : (
            <div
              className={`mt-4 mb-0 xl:m-0 lg:mb-0 text-right ml-auto block md:flex lg:flex ${
                isOfflineEvent && !hasAuthForm ? "hidden" : ""
              }`}
            >
              <button className="rounded-full ml-auto px-6 py-2 text-xs font-semibold  bg-green-500 items-center flex">
                <StatusOnlineIcon className="w-4 h-4 text-slate-50" />
                <span className="ml-2 text-white">{t("eventIsLife")}</span>
              </button>
            </div>
          )}
        </div>
        <div
          className={cx("pb-6 flex md:pb-4", {
            "flex-col": isOfflineEvent,
            "flex-col md:flex-row": !isOfflineEvent,
            "lg:hidden": isOfflineEvent && !isEnded,
          })}
        >
          <div className="flex flex-col sm:items-start basis-full md:pt-6">
            {isOfflineEvent && (
              <div className="lg:hidden mb-4 md:m-auto">
                <EventCoverPlayer
                  videoUrl={
                    event?.cover !== null ? cloudfront + event?.cover : null
                  }
                  adaptivePlaylistCover={event?.adaptivePlaylistCover}
                />
              </div>
            )}
            <div className="flex gap-4 items-center">
              <Link href={`/${publicId}#events`} passHref>
                <div className="relative mr-0 drop-shadow-4sm cursor-pointer">
                  <img
                    alt="Avatar"
                    src={avatar}
                    className="border-2 border-white bg-avatar-gradient w-12 h-12 sm:w-[60px] sm:h-[60px] object-cover rounded-full max-w-none"
                  />
                  <span
                    className={cx(
                      "absolute z-10 top-1 right-1 inline-flex items-center justify-center p-1 text-xs font-bold leading-none rounded-full border-2 border-white",
                      {
                        "bg-red-600": !isOnline,
                        "bg-green-600": isOnline,
                      }
                    )}
                  />
                </div>
              </Link>
              <h2 className="break-words max-w-full text-lg font-semibold leading-5 text-slate-900 sm:text-2xl">
                {event?.name}
              </h2>
            </div>
            <div className="py-3 flex flex-wrap gap-x-3 gap-y-1 font-medium text-sm text-slate-500">
              {promotionCode && (
                <span className="flex items-center first-letter:uppercase">
                  <ReceiptTaxIcon className="w-5 h-5 mr-1 text-custom-dark-blue stroke-2" />{" "}
                  <span className="font-semibold text-custom-dark-blue">
                    {code}
                  </span>
                </span>
              )}
              <span className="flex items-center first-letter:uppercase">
                <EventIcon className="shrink-0 w-5 h-5 mr-1" />
                <span className="first-letter:uppercase">{t("event")}</span>
              </span>
              <span className="flex items-center whitespace-nowrap">
                <>
                  <div>
                    <IconComponent className="shrink-0 w-5 h-5 mr-1 bg-slate-500 text-white" />
                  </div>
                </>

                <span
                  className={
                    (discountForSelectedLevel &&
                      discountForSelectedLevel?.discount !== 0) ||
                    (promotionCode &&
                      totalDiscount !== 0 &&
                      (isOnlineEvent || isOnlineTelegramEvent))
                      ? "line-through"
                      : ""
                  }
                >
                  {event?.levels[selectedPlan]?.price}
                </span>
                {promotionCode && totalDiscount !== 0 ? (
                  <span className="ml-1">
                    {priceAfterDiscount === 0 ? "0.00" : priceAfterDiscount}
                  </span>
                ) : null}
              </span>
              <span className="flex md:items-center xl:items-center">
                <CalendarIcon className="shrink-0 h-5 w-5 mr-1 text-slate-500" />
                <span>
                  {isOnlineEvent || isOnlineTelegramEvent ? (
                    <Tippy
                      content={
                        <span>
                          {isMounted
                            ? formattedDate(
                                event?.eventDate,
                                event?.eventDateEnd,
                                timeFormat,
                                event?.timezone || clientTimeZone
                              )
                            : ""}
                          {shouldShowTimeZoneCity ? (
                            <span className="ml-1">({event?.city})</span>
                          ) : null}
                        </span>
                      }
                      className="bg-black rounded-lg tippy-wrapper"
                      placement="bottom"
                    >
                      <div className="cursor-pointer">
                        {isMounted
                          ? formattedDate(
                              event?.eventDate,
                              event?.eventDateEnd,
                              timeFormat,
                              timeZone || clientTimeZone
                            )
                          : ""}
                      </div>
                    </Tippy>
                  ) : (
                    <div>
                      {isMounted
                        ? formattedDate(
                            event?.eventDate,
                            event?.eventDateEnd,
                            timeFormat,
                            event?.timezone
                          )
                        : ""}
                    </div>
                  )}
                </span>
              </span>
              <span className="flex items-center">
                {isOnlineEvent || isOnlineTelegramEvent ? (
                  <StatusOnlineIcon className="h-5 w-5 mr-1 text-gray-500" />
                ) : (
                  <LocationMarkerIcon className="h-5 w-5 mr-1 text-gray-500" />
                )}
                <ReadableEventLocationText text={eventPlaceLabel} />
              </span>
              {isSoldEnabled ? (
                <>
                  <span className="flex items-center custom-dark-blue">
                    <ShoppingCartIcon className="w-5 h-5 mr-1 text-custom-dark-blue" />
                    <span className="flex items-center custom-dark-blue">
                      {event?.sold}
                    </span>
                  </span>
                </>
              ) : null}
            </div>
          </div>
          <div
            className={cx(
              "flex basis-auto flex-row sm:flex-row sm:shrink-0 items-center justify-between md:gap-2",
              {
                "justify-between": isOfflineEvent,
              }
            )}
          >
            <div className="flex items-center gap-2">
              <Button
                onClick={handleFollow}
                type="rounded"
                className="lg:mr-0 md:mr-0"
                icon={<HeartIcon className="text-slate-500 h-4 w-4" />}
              />
              {/* <Button
                icon={<ChatIcon className="stroke-slate-500 h-4 w-4" />}
                type="rounded"
                onClick={openChat}
                className={
                  freeChat ? "!border-slate-500 text-slate-900 mr-0" : "hidden"
                }
              /> */}
              <OfferShareButton onClick={handleCopy} />
            </div>
            {isOfflineEvent && !isEnded && (
              <div className="hidden md:block">
                {loading ? null : !hasStarted ? (
                  <button className="rounded-full px-6 py-2 text-xs font-semibold bg-yellow-300 items-center flex">
                    <HourglassIcon color="black" className="shrink-0 w-4 h-4" />
                    {getRemainingTime(event?.eventDate, timeZone)}
                  </button>
                ) : isOngoing ? (
                  <button className="rounded-full px-6 py-2 text-xs font-semibold bg-green-500 items-center flex">
                    <StatusOnlineIcon className="w-4 h-4 text-slate-50" />
                    <span className="ml-2 text-white">{t("eventIsLife")}</span>
                  </button>
                ) : (
                  <button className="rounded-full px-6 py-2 text-xs font-semibold bg-slate-100 items-center flex">
                    <FlagIcon className="w-4 h-4" />
                    <span className="ml-1">{t("passed")}</span>
                  </button>
                )}
              </div>
            )}
            <div className="md:hidden">
              {loading ? null : !hasStarted ? (
                <button className="rounded-full px-6 py-2 text-xs font-semibold bg-yellow-300 items-center flex">
                  <HourglassIcon color="black" className="shrink-0 w-4 h-4" />
                  {getRemainingTime(event?.eventDate, timeZone)}
                </button>
              ) : isOngoing ? (
                <button className="rounded-full px-6 py-2 text-xs font-semibold bg-green-500 items-center flex">
                  <StatusOnlineIcon className="w-4 h-4 text-slate-50" />
                  <span className="ml-2 text-white">{t("eventIsLife")}</span>
                </button>
              ) : (
                <button className="rounded-full px-6 py-2 text-xs font-semibold bg-slate-100 items-center flex">
                  <FlagIcon className="w-4 h-4" />
                  <span className="ml-1">{t("passed")}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {!hasAuthForm &&
        !isEnded &&
        isOfflineEvent &&
        isAssignedSeatingEvent ? (
          <div className="lg:hidden mb-4">
            <div className="bg-slate-50 rounded-xl">
              <PublicAssignedSeatingPurchasePanel
                event={event}
                promotionCode={promotionCode}
                countryCurrency={countryCurrency}
                isLevelSoldOut={isLevelSoldOut}
                assignedSeatingTicketTypes={assignedSeatingTicketTypes}
                isSoldOut={isSoldOut(event?.levels)}
                isMobile={true}
                onOpen={() => setSeatPickerOpen(true)}
                handleOpenPromoModal={handleOpenPromoModal}
                translate={t}
              />
            </div>
          </div>
        ) : hasAuthForm && isOfflineEvent && !isEnded ? (
          <div className="lg:hidden mb-4">
            <div className="bg-slate-50 rounded-xl">
              {hasAuthForm === "sign-up" ? (
                <OfferSignInUpOtp
                  type="event"
                  handleAuthForm={setAuthForm}
                  emailRef={registerAuthEmailInput}
                  publicId={publicId}
                  offerId={eventId}
                  code={code}
                />
              ) : (
                <OfferSignIn
                  type="event"
                  handleAuthForm={setAuthForm}
                  emailRef={registerAuthEmailInput}
                  publicId={publicId}
                  offerId={eventId}
                  code={code}
                />
              )}
            </div>
          </div>
        ) : !hasAuthForm && !isEnded && isOfflineEvent ? (
          <div className="lg:hidden mb-4">
            <div className="bg-slate-50 rounded-xl">
              <h1 className="mb-5 text-base text-center">
                {t("selectTickets")}
              </h1>
              {event?.levels.map((plan, index) => {
                const discountLevel = promotionCode?.levels_discount.find(
                  (discount) => discount.levelid === plan.levelid
                );

                const discount = discountLevel ? discountLevel.discount : null;
                const isPlanSoldOut = isLevelSoldOut(plan);
                const seatingTicketType = assignedSeatingTicketTypes.find(
                  (ticketType) =>
                    ticketType.key === plan.levelid ||
                    ticketType.name === plan.name
                );

                return (
                  <div
                    key={plan.name}
                    className={`m-auto md:w-11/12 flex justify-between items-start rounded-xl mb-4 p-4 cursor-pointer ${
                      isPlanSoldOut ? "" : "border border-slate-500"
                    }`}
                    onClick={() => handlePlanChange(index)}
                  >
                    <div className="flex">
                      <EventTicketAvailabilityIndicator
                        color={seatingTicketType?.color}
                        isSoldOut={isPlanSoldOut}
                      />

                      <div className="text-base">
                        <div className="text-grey-900 font-medium">
                          {plan.name}
                        </div>
                        <div>
                          <span
                            className={`${
                              isPlanSoldOut ? "text-gray-300" : "text-gray-900"
                            } font-medium`}
                          >
                            {isPlanSoldOut
                              ? t("soldOut")
                              : countryCurrency?.symbol + plan.price}
                          </span>
                          {Number(discount) > 0 ? (
                            <span className="text-slate-500">
                              {" "}
                              (-{discount}%)
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <button
                        onClick={() => decrementCount(index)}
                        className={`border-gray-300 border cursor-pointer text-white font-bold py-2 px-2 rounded-l mr-2 ${
                          isPlanSoldOut ? "opacity-50" : ""
                        }`}
                      >
                        <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                      </button>
                      <span className="flex items-center mr-2">
                        {countEvent[index]}
                      </span>
                      <button
                        data-testid={`select-count-tickets-mobile[${index}]`}
                        onClick={() => incrementCount(index)}
                        disabled={isPlanSoldOut}
                        className={`border-gray-300 border cursor-pointer text-white font-bold py-2 px-2 rounded-r ${
                          isPlanSoldOut ? "opacity-50" : ""
                        }`}
                      >
                        <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                );
              })}

              <div className="flex flex-col items-center justify-center">
                {isSoldOut(event?.levels) ? (
                  <Button
                    className="w-3/4"
                    icon={<TicketIcon className="h-6 w-6" />}
                    type="sold"
                    disabled={true}
                    fluid
                  >
                    {t("soldOut")}
                  </Button>
                ) : totalSum === 0 &&
                  countEvent.every((count) => count === 0) ? (
                  <Button
                    className="w-3/4 text-sm"
                    icon={<TicketIcon className="h-6 w-6" />}
                    type="noTickets"
                    disabled={true}
                    fluid
                  >
                    {t("noTicketsSelected")}
                  </Button>
                ) : (
                  <>
                    <div
                      className="inline-flex justify-center w-3/4"
                      data-testid="buy-button-mobile"
                    >
                      <Button
                        className="w-3/4"
                        icon={<TicketIcon className="h-6 w-6" />}
                        type="danger"
                        onClick={handleBuy}
                        fluid
                      >
                        {totalSum === 0
                          ? t("attendForfree")
                          : `${t("buyFor")} ${
                              countryCurrency?.symbol
                            }${totalSum}`}
                      </Button>
                    </div>

                    {!promotionCode ? (
                      <EventPromotionAction
                        label={t("enterPromoCodePromotions")}
                        onClick={handleOpenPromoModal}
                      />
                    ) : null}
                  </>
                )}
                {isSoldEnabled && (
                  <div className="flex justify-center items-center mt-4 mb-2">
                    <UserGroupIcon className="w-5 h-5 mr-1" />
                    <span className="text-sm">
                      {seatsLeftAllLevels(event?.levels)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
        <div
          className={
            hasAuthForm || (!isEnded && isOfflineEvent)
              ? "lg:grid grid-cols-12 gap-4"
              : null
          }
        >
          <div
            className={cx("shadow rounded-xl pt-4 px-4 pb-4", {
              "col-span-7 bg-slate-50 p-4 mb-4 order-last":
                !isEnded && isOfflineEvent && !hasAuthForm,
              "col-span-7 mb-4 order-last":
                !isEnded && isOfflineEvent && hasAuthForm,
              "col-span-8 mb-4 lg:mb-0":
                hasAuthForm && (isOnlineEvent || isOnlineTelegramEvent),
              "max-w-3xl lg:max-w-4xl mb-4 lg:mb-0":
                !hasAuthForm && (isOnlineEvent || isOnlineTelegramEvent),
            })}
          >
            {!isEnded && isOfflineEvent && (
              <div className="hidden lg:flex pb-6 md:pb-4 flex-col">
                <div className="flex flex-col sm:items-start basis-full">
                  <div className="flex gap-4 items-center">
                    <Link href={`/${publicId}#events`} passHref>
                      <div className="relative mr-0 drop-shadow-4sm cursor-pointer">
                        <img
                          alt="Avatar"
                          src={avatar}
                          className="border-2 border-white bg-avatar-gradient w-12 h-12 sm:w-[60px] sm:h-[60px] object-cover rounded-full max-w-none"
                        />
                        <span
                          className={cx(
                            "absolute z-10 top-1 right-1 inline-flex items-center justify-center p-1 text-xs font-bold leading-none rounded-full border-2 border-white",
                            {
                              "bg-red-600": !isOnline,
                              "bg-green-600": isOnline,
                            }
                          )}
                        />
                      </div>
                    </Link>
                    <h2 className="break-words max-w-full text-lg font-semibold leading-5 text-slate-900 sm:text-2xl">
                      {event?.name}
                    </h2>
                  </div>
                  <div className="py-3 flex flex-wrap gap-x-3 gap-y-1 font-medium text-sm text-slate-500">
                    {promotionCode && (
                      <span className="flex items-center first-letter:uppercase">
                        <ReceiptTaxIcon className="w-5 h-5 mr-1 text-custom-dark-blue stroke-2" />{" "}
                        <span className="font-semibold text-custom-dark-blue">
                          {code}
                        </span>
                      </span>
                    )}
                    <span className="flex items-center first-letter:uppercase">
                      <EventIcon className="shrink-0 w-5 h-5 mr-1" />
                      <span className="first-letter:uppercase">
                        {t("event")}
                      </span>
                    </span>
                    <span className="flex items-center whitespace-nowrap">
                      <>
                        <div>
                          <IconComponent className="shrink-0 w-5 h-5 mr-1 bg-slate-500 text-white" />
                        </div>
                      </>

                      <span
                        className={
                          (discountForSelectedLevel &&
                            discountForSelectedLevel?.discount !== 0) ||
                          (promotionCode && totalDiscount !== 0)
                            ? "line-through"
                            : ""
                        }
                      >
                        {event?.levels[selectedPlan]?.price}
                      </span>
                      {promotionCode && totalDiscount !== 0 ? (
                        <span className="ml-1">
                          {priceAfterDiscount === 0
                            ? "0.00"
                            : priceAfterDiscount}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex md:items-center xl:items-center">
                      <CalendarIcon className="shrink-0 h-5 w-5 mr-1 text-slate-500" />
                      <span>
                        <div>
                          {isMounted
                            ? formattedDate(
                                event?.eventDate,
                                event?.eventDateEnd,
                                timeFormat,
                                event?.timezone
                              )
                            : ""}
                          {shouldShowTimeZoneCity ? (
                            <span className="ml-1">({event?.city})</span>
                          ) : null}
                        </div>
                      </span>
                    </span>
                    <span className="flex items-center">
                      <LocationMarkerIcon className="h-5 w-5 mr-1 text-gray-500" />
                      <ReadableEventLocationText text={eventPlaceLabel} />
                    </span>
                    {isSoldEnabled ? (
                      <>
                        <span className="flex items-center custom-dark-blue">
                          <ShoppingCartIcon className="w-5 h-5 mr-1 text-custom-dark-blue" />
                          <span className="flex items-center custom-dark-blue">
                            {event?.sold}
                          </span>
                        </span>
                      </>
                    ) : null}
                    {isOfflineEvent && isEnded && (
                      <span className="hidden md:flex items-center">
                        {loading ? null : !hasStarted ? (
                          <HourglassIcon
                            color="black"
                            className="shrink-0 w-5 h-5 mr-1"
                          />
                        ) : isOngoing ? (
                          <StatusOnlineIcon className="h-5 w-5 mr-1 text-green-500" />
                        ) : (
                          <FlagIcon className="h-5 w-5 mr-1 text-slate-500" />
                        )}
                        <span>
                          {loading
                            ? ""
                            : !hasStarted
                            ? getRemainingTime(event?.eventDate, timeZone)
                            : isOngoing
                            ? t("eventIsLife")
                            : t("passed")}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div
                  className={cx(
                    "flex basis-auto flex-row sm:flex-row sm:shrink-0 items-center gap-2",
                    {
                      "justify-between": isOfflineEvent,
                      "pt-6": event?.eventType !== "OfflineEvent",
                    }
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleFollow}
                      type="rounded"
                      className="lg:mr-0 md:mr-0"
                      icon={<HeartIcon className="text-slate-500 h-4 w-4" />}
                    />
                    <OfferShareButton onClick={handleCopy} />
                  </div>
                  {isOfflineEvent && (
                    <>
                      {loading ? null : !hasStarted ? (
                        <button className="rounded-full px-6 py-2 text-xs font-semibold bg-yellow-300 items-center flex">
                          <HourglassIcon
                            color="black"
                            className="shrink-0 w-4 h-4"
                          />
                          {getRemainingTime(event?.eventDate, timeZone)}
                        </button>
                      ) : isOngoing ? (
                        <button className="rounded-full px-6 py-2 text-xs font-semibold bg-green-500 items-center flex">
                          <StatusOnlineIcon className="w-4 h-4 text-slate-50" />
                          <span className="ml-2 text-white">
                            {t("eventIsLife")}
                          </span>
                        </button>
                      ) : (
                        <button className="rounded-full px-6 py-2 text-xs font-semibold bg-slate-100 items-center flex">
                          <FlagIcon className="w-4 h-4" />
                          <span className="ml-1">{t("passed")}</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {event?.description ? (
              <section className="mb-6 pt-4 sm:border-t sm:border-slate-200">
                <h2 className="mb-3 hidden text-base font-semibold text-slate-900 sm:block">
                  {t("aboutTheEventOffer")}
                </h2>
                <div className="text-sm whitespace-pre-line break-words">
                  {makeUrlsClickable(event?.description)}
                </div>
              </section>
            ) : null}
            <EventRefundPolicyNotice
              enabled={event?.sections?.refundPolicy}
              compact
            />
            {isOfflineEvent ? (
              <EventLocationSection
                location={event?.sections?.location}
                locationDetails={event?.sections?.locationDetails}
                address={event?.sections?.address}
                city={event?.city}
                locationVisibility={event?.sections?.locationVisibility}
                eventDate={event?.eventDate}
                isPurchased={false}
                t={t}
              />
            ) : null}
            {(isOnlineEvent || isOnlineTelegramEvent) && (
              <OfferAttachment
                attachemntUrl={
                  event?.cover !== null ? cloudfront + event?.cover : null
                }
                adaptivePlaylistCover={event?.adaptivePlaylistCover}
                cover={true}
              />
            )}
            {isOfflineEvent && isEnded && (
              <div className="hidden lg:block">
                <OfferAttachment
                  attachemntUrl={
                    event?.cover !== null ? cloudfront + event?.cover : null
                  }
                  adaptivePlaylistCover={event?.adaptivePlaylistCover}
                  cover={true}
                />
              </div>
            )}

            <EventOrganizerLinks
              publicId={
                event?.expertPublicId ||
                event?.expert?.publicId ||
                expert?.publicId ||
                publicId
              }
              name={
                event?.expertName ||
                getFullName(
                  expert?.firstName,
                  expert?.lastName,
                  expert?.publicId
                )
              }
              headline={
                event?.expertHeadline ||
                event?.expert?.occupation ||
                expert?.occupation
              }
              avatar={
                event?.expertAvatar
                  ? cloudfront + event.expertAvatar
                  : event?.expert?.avatar
                  ? cloudfront + event.expert.avatar
                  : expert?.avatar
                  ? cloudfront + expert.avatar
                  : null
              }
              email={
                event?.expertEmail || event?.expert?.email || expert?.email
              }
              phone={
                event?.expertPhone || event?.expert?.phone || expert?.phone
              }
              socialNetworks={
                event?.expertSocialNetworks ||
                event?.expert?.socialNetworks ||
                expert?.socialNetworks
              }
              expertId={event?.expertId}
              isFollowed={false}
              handleFollow={handleFollow}
              showFollow={!hasAuthForm}
            />

            {hasAuthForm ? null : isEnded ? (
              <div className="mt-4 mb-2 max-w-[270px] md:max-w-[300px] mx-auto">
                <div className="justify-center items-center gap-2 w-full text-white border border-transparent rounded-lg shadow-sm py-2 px-6 inline-flex font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 bg-slate-100">
                  <TicketIcon className="w-6 h-6 text-black" />
                  <span className="text-black text-sm">{t("eventIsOver")}</span>
                </div>
              </div>
            ) : isSoldOut(event?.levels) &&
              !isEnded &&
              (isOnlineEvent || isOnlineTelegramEvent) ? (
              <div className="mt-4 mb-2 max-w-[270px] md:max-w-[300px] mx-auto">
                <Button
                  icon={<TicketIcon className="h-6 w-6 " />}
                  type="sold"
                  fluid
                >
                  {t("soldOut")}
                </Button>
                {isSoldEnabled && (
                  <div className="flex justify-center items-center mt-4 mb-2">
                    <UserGroupIcon className="w-5 h-5 mr-1" />
                    <span className="text-sm">
                      {seatsLeftAllLevels(event?.levels)}
                    </span>
                  </div>
                )}
              </div>
            ) : !isEnded &&
              (isOnlineEvent || isOnlineTelegramEvent) &&
              !hasAuthForm ? (
              <div className="mt-4 mb-2 max-w-[270px] md:max-w-[300px] mx-auto">
                <div data-testid="buy-button">
                  {" "}
                  <Button
                    icon={<TicketIcon className="h-6 w-6" />}
                    type="danger"
                    onClick={handleBuy}
                    fluid
                  >
                    {isFreeEvent
                      ? t("attendForfree")
                      : `${t("buyFor")} ${countryCurrency?.symbol}${
                          priceAfterDiscount * countEvent[selectedPlan]
                        }`}
                  </Button>
                </div>

                {!promotionCode ? (
                  <div className="flex justify-center cursor-pointer text-custom-dark-blue mt-4 text-sm">
                    <button onClick={handleOpenPromoModal}>
                      + {t("enterPromoCodePromotions")}
                    </button>
                  </div>
                ) : null}
                {isSoldEnabled && (
                  <div className="flex justify-center items-center mt-4 mb-2">
                    <UserGroupIcon className="w-5 h-5 mr-1" />
                    <span className="text-sm">
                      {seatsLeftAllLevels(event?.levels)}
                    </span>
                  </div>
                )}
              </div>
            ) : hasAuthForm && (isOnlineEvent || isOnlineTelegramEvent) ? (
              <div className="mt-4 mb-4 max-w-[270px] md:max-w-[300px] mx-auto">
                <div className="bg-slate-50 p-4 shadow rounded-xl">
                  {hasAuthForm === "sign-up" ? (
                    <OfferSignInUpOtp
                      type="event"
                      handleAuthForm={setAuthForm}
                      emailRef={registerAuthEmailInput}
                      publicId={publicId}
                      offerId={eventId}
                      code={code}
                    />
                  ) : (
                    <OfferSignIn
                      type="event"
                      handleAuthForm={setAuthForm}
                      emailRef={registerAuthEmailInput}
                      publicId={publicId}
                      offerId={eventId}
                      code={code}
                    />
                  )}
                </div>
              </div>
            ) : null}
          </div>
          {isOfflineEvent && (
            <div className="flex items-center justify-center gap-2 mt-6 mb-4 lg:hidden">
              <Link href="/sign-up" className="flex items-center gap-2 ">
                <span className="text-gray-400 text-sm">
                  {t("createEventOn")}
                </span>
                <img
                  src="/img/logo/logo-client.svg"
                  alt="waylight"
                  className="h-6"
                />
              </Link>
            </div>
          )}
          {hasAuthForm && (isOnlineEvent || isOnlineTelegramEvent) ? (
            <div className="mx-auto col-span-4 rounded-xl sm:rounded-lg lg:shadow md:p-4 lg:p-4 max-w-md lg:min-w-full mb-auto">
              {hasAuthForm === "sign-up" ? (
                <OfferSignInUpOtp
                  type="event"
                  handleAuthForm={setAuthForm}
                  emailRef={registerAuthEmailInput}
                  publicId={publicId}
                  offerId={eventId}
                  code={code}
                />
              ) : (
                <OfferSignIn
                  type="event"
                  handleAuthForm={setAuthForm}
                  emailRef={registerAuthEmailInput}
                  publicId={publicId}
                  offerId={eventId}
                  code={code}
                />
              )}
            </div>
          ) : !isEnded && isOfflineEvent ? (
            <div className="col-span-4 order-first hidden lg:block">
              <div className="lg:bg-slate-50 p-4 lg:shadow lg:rounded-xl mb-4">
                <div className={hasAuthForm ? "mb-6" : ""}>
                  <EventCoverPlayer
                    videoUrl={
                      event?.cover !== null ? cloudfront + event?.cover : null
                    }
                    adaptivePlaylistCover={event?.adaptivePlaylistCover}
                  />
                </div>
                {hasAuthForm ? (
                  hasAuthForm === "sign-up" ? (
                    <OfferSignInUpOtp
                      type="event"
                      handleAuthForm={setAuthForm}
                      emailRef={registerAuthEmailInput}
                      publicId={publicId}
                      offerId={eventId}
                      code={code}
                      eventType={event?.eventType}
                    />
                  ) : (
                    <OfferSignIn
                      type="event"
                      handleAuthForm={setAuthForm}
                      emailRef={registerAuthEmailInput}
                      publicId={publicId}
                      offerId={eventId}
                      code={code}
                    />
                  )
                ) : isAssignedSeatingEvent ? (
                  <PublicAssignedSeatingPurchasePanel
                    event={event}
                    promotionCode={promotionCode}
                    countryCurrency={countryCurrency}
                    isLevelSoldOut={isLevelSoldOut}
                    assignedSeatingTicketTypes={assignedSeatingTicketTypes}
                    isSoldOut={isSoldOut(event?.levels)}
                    isMobile={false}
                    onOpen={() => setSeatPickerOpen(true)}
                    handleOpenPromoModal={handleOpenPromoModal}
                    translate={t}
                  />
                ) : (
                  <>
                    <h1 className="mb-5 text-base text-center pt-6">
                      {t("selectTickets")}
                    </h1>
                    {event?.levels.map((plan, index) => {
                      const discountLevel = promotionCode?.levels_discount.find(
                        (discount) => discount.levelid === plan.levelid
                      );

                      const discount = discountLevel
                        ? discountLevel.discount
                        : null;
                      const isPlanSoldOut = isLevelSoldOut(plan);
                      const seatingTicketType = assignedSeatingTicketTypes.find(
                        (ticketType) =>
                          ticketType.key === plan.levelid ||
                          ticketType.name === plan.name
                      );

                      return (
                        <div
                          key={plan.name}
                          className={`m-auto w-11/12 flex justify-between items-start shadow rounded-xl mb-4 p-4 cursor-pointer ${
                            isPlanSoldOut ? "" : "border border-slate-500"
                          }`}
                          onClick={() => handlePlanChange(index)}
                        >
                          <div className="flex">
                            <EventTicketAvailabilityIndicator
                              color={seatingTicketType?.color}
                              isSoldOut={isPlanSoldOut}
                            />

                            <div className="text-base">
                              <div className="text-grey-900 font-medium">
                                {plan.name}
                              </div>
                              <div>
                                <span
                                  className={`${
                                    isPlanSoldOut
                                      ? "text-gray-300"
                                      : "text-gray-900"
                                  } font-medium`}
                                >
                                  {isPlanSoldOut
                                    ? t("soldOut")
                                    : countryCurrency?.symbol + plan.price}
                                </span>
                                {Number(discount) > 0 ? (
                                  <span className="text-slate-500">
                                    {" "}
                                    (-{discount}%)
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-center">
                            <button
                              onClick={() => decrementCount(index)}
                              className={`border-gray-300 border cursor-pointer text-white font-bold py-2 px-2 rounded-l mr-2 ${
                                isPlanSoldOut ? "opacity-50" : ""
                              }`}
                            >
                              <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                            </button>
                            <span className="flex items-center mr-2">
                              {countEvent[index]}
                            </span>
                            <button
                              data-testid={`select-count-tickets[${index}]`}
                              onClick={() => incrementCount(index)}
                              disabled={isPlanSoldOut}
                              className={`border-gray-300 border cursor-pointer text-white font-bold py-2 px-2 rounded-r ${
                                isPlanSoldOut ? "opacity-50" : ""
                              }`}
                            >
                              <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div className="flex flex-col items-center justify-center">
                      {isSoldOut(event?.levels) ? (
                        <Button
                          className={`w-3/4`}
                          icon={<TicketIcon className="h-6 w-6 " />}
                          type="sold"
                          disabled={true}
                          fluid
                        >
                          {t("soldOut")}
                        </Button>
                      ) : totalSum === 0 &&
                        countEvent.every((count) => count === 0) ? (
                        <Button
                          className={`w-3/4 text-sm`}
                          icon={<TicketIcon className="h-6 w-6" />}
                          type="noTickets"
                          disabled={true}
                          fluid
                        >
                          {t("noTicketsSelected")}
                        </Button>
                      ) : (
                        <>
                          <div
                            className="
     inline-flex  justify-center w-3/4"
                            data-testid="buy-button"
                          >
                            <Button
                              className={`w-3/4`}
                              icon={<TicketIcon className="h-6 w-6" />}
                              type="danger"
                              onClick={handleBuy}
                              fluid
                            >
                              {totalSum === 0
                                ? t("attendForfree")
                                : `${t("buyFor")} ${
                                    countryCurrency?.symbol
                                  }${totalSum}`}
                            </Button>
                          </div>

                          {!promotionCode ? (
                            <EventPromotionAction
                              label={t("enterPromoCodePromotions")}
                              onClick={handleOpenPromoModal}
                            />
                          ) : null}
                        </>
                      )}
                      {isSoldEnabled && (
                        <div className="flex justify-center items-center mt-4 mb-2">
                          <UserGroupIcon className="w-5 h-5 mr-1" />
                          <span className="text-sm">
                            {seatsLeftAllLevels(event?.levels)}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center justify-center gap-2 mt-4">
                <Link href="/sign-up" className="flex items-center gap-2 mb-4">
                  <span className="text-gray-400 text-sm">
                    {t("createEventOn")}
                  </span>
                  <img
                    src="/img/logo/logo-client.svg"
                    alt="waylight"
                    className="h-6"
                  />
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <AssignedSeatingPicker
        isOpen={isSeatPickerOpen}
        isLoading={false}
        isHolding={false}
        seatMap={assignedSeatingMap}
        ticketTypes={assignedSeatingTicketTypes}
        selectedSeats={selectedSeats}
        promotionCode={promotionCode}
        eventLevels={getEventLevels(event)}
        currencySymbol={countryCurrency?.symbol}
        eventName={event?.name}
        translate={t}
        onClose={() => setSeatPickerOpen(false)}
        onSeatToggle={handleAnonymousSeatToggle}
        onSeatRemove={handleAnonymousSeatToggle}
        onBuy={handleAnonymousSeatBuy}
        onRetry={() => undefined}
      />
      <Modal
        isOpen={isPromoModalOpen}
        showCloseBtn={true}
        title={t("enterPromoCodePromotions")}
        icon="promotion"
        onClose={promoModalClose}
      >
        <div className="relative mb-6">
          <ReceiptTaxIcon className="absolute left-3 top-[12px] transform -t-y-1/2 h-5 w-5 text-slate-500" />
          <input
            className="shadow appearance-none border border-slate-300 text-gray-900 rounded w-full py-3 pl-10 pr-3 text-sm font-semibold leading-tight focus:outline-none focus:shadow-outline"
            id="promo-code"
            type="text"
            value={promo}
            onChange={(e) => setPromo(e.target.value)}
          />
        </div>
        <PromotionModalActions
          cancelLabel={t("buttonCancel")}
          confirmLabel={t("confirm")}
          loading={checkPromotionLoader}
          onCancel={promoModalClose}
          onConfirm={handleCheckPromotion}
        />
      </Modal>
    </>
  );
};

export default EventPage;
